import React from "react";
import { cn } from "@/lib/utils";

/**
 * Toggle — switch-style boolean control.
 *
 * Implemented as a `role="switch"` button (not `<input type=checkbox>`)
 * so the AT announcement reads "on"/"off" rather than "checked".
 *
 * Props:
 *   • checked            — controlled state
 *   • defaultChecked     — uncontrolled initial state
 *   • onCheckedChange(b) — fired on change
 *   • label              — text rendered to the right of the track
 *   • description        — optional sub-label
 */

export const Toggle = React.forwardRef(function Toggle(
  {
    checked,
    defaultChecked,
    onCheckedChange,
    label,
    description,
    disabled = false,
    className,
    id,
    "data-testid": testId,
    ...rest
  },
  ref
) {
  const isControlled = checked !== undefined;
  const [internal, setInternal] = React.useState(Boolean(defaultChecked));
  const isOn = isControlled ? Boolean(checked) : internal;
  const generatedId = React.useId();
  const buttonId = id || generatedId;
  const descId = description ? `${buttonId}-desc` : undefined;

  const handleClick = () => {
    if (disabled) return;
    const next = !isOn;
    if (!isControlled) setInternal(next);
    if (typeof onCheckedChange === "function") onCheckedChange(next);
  };

  return (
    <div className={cn("inline-flex items-start gap-3", className)}>
      <button
        ref={ref}
        id={buttonId}
        type="button"
        role="switch"
        aria-checked={isOn}
        aria-describedby={descId}
        disabled={disabled}
        onClick={handleClick}
        data-testid={testId}
        data-state={isOn ? "on" : "off"}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-[var(--ds-radius-pill)] border",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ds-color-background)]",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          isOn
            ? "bg-[var(--ds-color-accent)] border-[var(--ds-color-accent)]"
            : "bg-[var(--ds-color-surface-inset)] border-[var(--ds-color-border-strong)]"
        )}
        style={{ transitionDuration: "var(--ds-duration-fast)" }}
      >
        <span
          aria-hidden
          className={cn(
            "block h-3.5 w-3.5 rounded-[var(--ds-radius-pill)] bg-[var(--ds-color-surface)]",
            "transition-transform",
            isOn ? "translate-x-[18px]" : "translate-x-[2px]"
          )}
          style={{ transitionDuration: "var(--ds-duration-fast)" }}
        />
      </button>
      {(label || description) && (
        <label
          htmlFor={buttonId}
          className={cn(
            "flex flex-col leading-snug text-[14px] text-[var(--ds-color-ink)]",
            !disabled && "cursor-pointer"
          )}
        >
          {label && <span>{label}</span>}
          {description && (
            <span
              id={descId}
              className="text-[13px] text-[var(--ds-color-ink-muted)]"
            >
              {description}
            </span>
          )}
        </label>
      )}
    </div>
  );
});

Toggle.displayName = "DSToggle";

export default Toggle;
